import { TableHeader } from "@/components/tables/add-header";
import type { DropdownItem } from "@/components/tables/data-row";
import { DataTable } from "@/components/tables/data-table";
import { ConfirmModal } from "@/components/ui/confirm-modal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MEETING_TYPES, type MeetingType } from "@/lib/meetings-constants";
import { useTRPC } from "@/lib/trpc";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { useState } from "react";

// Type for the meeting response
type MeetingResponse = {
  id: string;
  title: string;
  date: Date | string;
  meetingType: string;
  meetingSubtype: string | null;
  location: string | null;
  status: string;
  agendaMedia: {
    id: string;
    title: string;
    cloudStorageObject: {
      fullUrl: string;
    };
  } | null;
  minutesMedia: {
    id: string;
    title: string;
    cloudStorageObject: {
      fullUrl: string;
    };
  } | null;
};

const columns: ColumnDef<MeetingResponse>[] = [
  {
    accessorKey: "date",
    header: "Date",
    cell: ({ row }) => {
      const date = new Date(row.getValue("date"));
      return format(date, "MMM d, yyyy");
    },
  },
  {
    accessorKey: "title",
    header: "Title",
  },
  {
    accessorKey: "meetingType",
    header: "Type",
    cell: ({ row }) => {
      const meetingType = row.getValue("meetingType") as MeetingType;
      const type = MEETING_TYPES.find((t) => t.value === meetingType);
      return type?.label || meetingType;
    },
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      const status = row.getValue("status") as string;
      const statusClass =
        status === "completed"
          ? "text-green-600 bg-green-50"
          : status === "cancelled"
            ? "text-red-600 bg-red-50"
            : "text-blue-600 bg-blue-50";
      return (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClass}`}
        >
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      );
    },
  },
  {
    id: "documents",
    header: "Documents",
    cell: ({ row }) => {
      const meeting = row.original;
      return (
        <div className="flex gap-2">
          {meeting.agendaMedia ? (
            <a
              href={meeting.agendaMedia.cloudStorageObject.fullUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 hover:underline"
            >
              Agenda
            </a>
          ) : (
            <span className="text-xs text-gray-400">No agenda</span>
          )}
          <span className="text-gray-300">|</span>
          {meeting.minutesMedia ? (
            <a
              href={meeting.minutesMedia.cloudStorageObject.fullUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 hover:underline"
            >
              Minutes
            </a>
          ) : (
            <span className="text-xs text-gray-400">No minutes</span>
          )}
        </div>
      );
    },
  },
];

function MeetingsRoute() {
  const [selectedType, setSelectedType] = useState<MeetingType | undefined>();
  const [deleteMeetingId, setDeleteMeetingId] = useState<string | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const trpc = useTRPC();

  const meetingsQuery = useSuspenseQuery(
    trpc.meetings.list.queryOptions(
      selectedType ? { meetingType: selectedType } : undefined,
    ),
  );
  const meetings = meetingsQuery.data as MeetingResponse[];

  const deleteMutation = useMutation(
    trpc.meetings.delete.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["meetings"] });
        queryClient.invalidateQueries({ queryKey: ["media"] });
        setDeleteMeetingId(null);
      },
    }),
  );

  const dropdownItems: DropdownItem<MeetingResponse>[] = [
    {
      label: "Edit",
      onClick: ({ id }) => {
        navigate({
          to: `/meetings/${id}`,
        });
      },
    },
    {
      label: "Delete",
      onClick: ({ id }) => {
        setDeleteMeetingId(id);
      },
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <TableHeader linkTo="/meetings/new" buttonText="Create Meeting" />
        <div className="flex items-center gap-2">
          <Select
            value={selectedType || "all"}
            onValueChange={(value) =>
              setSelectedType(
                value === "all" ? undefined : (value as MeetingType),
              )
            }
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {MEETING_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <DataTable
        data={meetings}
        columns={columns}
        title="Meetings & Agendas"
        description={`Manage your meetings and associated documents ${selectedType ? `(${MEETING_TYPES.find((t) => t.value === selectedType)?.label})` : ""}`}
        dropdownItems={dropdownItems}
      />
      <ConfirmModal
        open={Boolean(deleteMeetingId)}
        onConfirm={() => {
          if (deleteMeetingId) deleteMutation.mutate({ id: deleteMeetingId });
        }}
        onDismiss={() => {
          setDeleteMeetingId(null);
        }}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}

export const Route = createFileRoute("/_authenticated/meetings/")({
  component: () => <MeetingsRoute />,
});
